import React, { useEffect, useState } from 'react';
import { Autocomplete, Checkbox, FormControlLabel, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import NotificationModal from '../../../../common/ModalWindows/NotificationModal';
import { responsiveSizeTimesheet } from '../../../../../utils/constants';
import { useWindowSize } from '../../../../../utils/hooks';
import MainButton from '../../../../common/buttons/MainButton';
import CustomDayPicker from '../../../../common/CustomDayPicker/CustomDayPicker';
import colors from '../../../../../scss/colors';
import CommonFileUploader from '../../../../fileupload/CommonFileUploader/CommonFileUploader';
import { AttachmentItem } from '../../../timesheet/Attachments/Attachments';
import classes from './ExpenseDetailMobile.module.scss';
import { generalEnums } from '../../../../../utils/enums';
import { buttonVariants } from '../../../../common/buttons/types';

const textFieldStyles = (width, radius) => ({
  borderRadius: radius,
  width,
  background: colors.WHITE,
  color: colors.BLACK,
  fieldset: {
    border: `1px solid ${colors.LIGHT_GREY}`,
    borderRadius: radius,
    legend: {
      width: 'unset'
    }
  }
});

const textAreaStyles = () => ({
  ...textFieldStyles(),
  width: '100%',
  '& .MuiOutlinedInput-input.MuiInputBase-input': {
    height: '50px'
  }
});

export default function ExpenseDetailMobile({ expenseCurrency, getStateData }) {
  const [category, setCategory] = useState('');
  const [currency, setCurrency] = useState('');
  const [checked, setChecked] = useState(false);
  const size = useWindowSize();
  const [showCurrency, setShowCurrency] = useState(null);
  const [fxRate, setFxRate] = useState(1);
  const [error, setError] = useState('');
  const currencies = useSelector((state) => state.constFromApi.currencies);
  const expensetypes = useSelector((state) => state.constFromApi.expensetypes);
  const { t } = useTranslation();

  const [expenseData, setExpenseData] = useState({
    CategoryId: '',
    Date: dayjs().format(generalEnums.DATEWITH_MMM),
    Comment: '',
    ExpenseAmount: '',
    ExpenseCurrency: {
      CurrencyName: ''
    },
    FxAmount: '',
    FxCurrency: {
      CurrencyName: ''
    },
    attachments: [],
    ExpenseAttachments: [],
    PayCurrency: expenseCurrency,
    isChecked: checked
  });

  const uploadHandler = (file) => {
    setExpenseData((prev) => ({
      ...prev,
      ExpenseAttachments: [...file],
      attachments: [...file],
      isChecked: false
    }));
  };

  const handleNoReceipt = (e) => {
    setChecked(e.target.checked);
    setExpenseData((prev) => ({
      ...prev,
      ExpenseAttachments: [],
      attachments: [],
      isChecked: true
    }));
  };

  const deleteHandler = (attach) => {
    const filteredFiles = [...expenseData.attachments].filter((file) => file.name !== attach.name);
    setExpenseData((prev) => ({
      ...prev,
      ExpenseAttachments: filteredFiles,
      attachments: filteredFiles,
      isChecked: false
    }));
  };

  useEffect(() => {
    getStateData(expenseData);
  }, [expenseData]);

  return (
    <div className={classes.mobileItemWrapper}>
      <div className={classes.mobileItemWrapperSection}>
        <h5 className={classes.title}>{t('dashboard.expenses.table.category')}*</h5>
        <div className={classes.content}>
          {expensetypes?.map((cat) => {
            return (
              <MainButton
                size="small"
                variant={
                  cat.label === (category.label || expenseData?.Category?.Name)
                    ? buttonVariants.SELECTED
                    : buttonVariants.TERNARY
                }
                outlined={cat.label !== (category.label || expenseData?.Category?.Name)}
                rounded
                key={cat.key}
                action={() =>
                  setExpenseData((prev) => ({
                    ...prev,
                    CategoryId: cat.value,
                    Category: {
                      Name: cat.label
                    }
                  }))
                }
              >
                {cat.label}
              </MainButton>
            );
          })}
        </div>
      </div>
      <div className={classes.mobileItemWrapperSection}>
        <h5 className={classes.title}>
          {expenseData?.CategoryId === 5 || category.value === 5
            ? t('dashboard.expenses.table.mileage')
            : t('dashboard.expenses.table.expenseamount')}
          *
        </h5>
        <div className={classes.content}>
          {expenseData?.CategoryId === 5 || category.value === 5 ? (
            <>
              <TextField
                type="number"
                disabled={
                  expenseData?.CategoryId !== 5 &&
                  (expenseData?.FxCurrency?.CurrencyName
                    ? expenseData?.ExpenseCurrency?.CurrencyName === expenseData?.FxCurrency?.CurrencyName
                    : true)
                }
                placeholder="Rate"
                value={expenseData?.FxAmount || null}
                onChange={(e) => {
                  setExpenseData((prev) => ({
                    ...prev,
                    FxAmount: e.target.value
                  }));
                }}
                sx={textFieldStyles('80px', '10px 0px 0px 10px')}
              />
              <TextField
                type="number"
                placeholder={
                  expenseData?.CategoryId === 5 || category.value === 5
                    ? t('common.general.miles')
                    : t('dashboard.expenses.table.amount')
                }
                value={expenseData?.ExpenseAmount || null}
                onChange={(e) => {
                  setExpenseData((prev) => ({
                    ...prev,
                    ExpenseAmount: e.target.value
                  }));
                }}
                sx={textFieldStyles('85px', '0px 10px 10px 0px')}
              />
            </>
          ) : (
            <>
              <TextField
                type="number"
                placeholder="0"
                value={expenseData?.ExpenseAmount}
                onChange={(e) => {
                  setExpenseData((prev) => ({
                    ...prev,
                    ExpenseAmount: e.target.value
                  }));
                }}
                sx={textFieldStyles('85px', '10px 0px 0px 10px')}
              />
              <Autocomplete
                disableClearable
                value={
                  (currency === expenseCurrency ? expenseCurrency : expenseData?.FxCurrency?.CurrencyName) ||
                  expenseData?.ExpenseCurrency?.CurrencyName ||
                  expenseCurrency
                }
                options={currencies}
                sx={textFieldStyles('100px', '0px 10px 10px 0px')}
                popupIcon={<ExpandMoreIcon />}
                forcePopupIcon
                renderOption={(props, option) => (
                  <li {...props} key={option.key}>
                    {option.label}
                  </li>
                )}
                onChange={(event, newValue) => {
                  setExpenseData((prev) => ({
                    ...prev,
                    FxCurrency: {
                      CurrencyName: newValue.label
                    },
                    FxCurrencyId: newValue.key || ''
                  }));
                  if (newValue.label !== expenseCurrency) {
                    setShowCurrency(true);
                  } else {
                    setFxRate(1);
                    setCurrency(expenseData?.ExpenseCurrency?.CurrencyName);
                    setExpenseData((prev) => ({
                      ...prev,
                      FxAmount: 1
                    }));
                  }
                }}
                renderInput={(params) => <TextField {...params} sx={textFieldStyles('100%')} />}
              />
            </>
          )}

          <p className={classes.expenseCurrencyValue}>
            = {`${expenseData.ExpenseAmount * (expenseData.FxAmount || 1)} ${expenseCurrency}`}
          </p>
        </div>
        <NotificationModal
          open={showCurrency}
          onClose={() => setShowCurrency(false)}
          paddingX={size.width > responsiveSizeTimesheet ? '36px' : '49px'}
          height={size.width > responsiveSizeTimesheet ? '450px' : '600px'}
          gap="44px"
        >
          <p className={classes.expenseCurrencyText}>{t('dashboard.createclaim.steps.currencychange')}</p>
          <div className={classes.filedsContainer}>
            <TextField
              type="number"
              disabled={
                expenseData?.CategoryId === 5 &&
                (expenseData?.FxCurrency?.CurrencyName
                  ? expenseData?.ExpenseCurrency?.CurrencyName === expenseData?.FxCurrency?.CurrencyName
                  : true)
              }
              placeholder="Rate"
              value={expenseData?.FxAmount || 1}
              onChange={(e) => {
                setExpenseData((prev) => ({
                  ...prev,
                  FxAmount: e.target.value
                }));
              }}
              sx={textFieldStyles('80px', '10px 0px 0px 10px')}
            />
            <span className={classes.expenseCurrencyText}>{expenseData?.PayCurrency}</span>
          </div>
          <div>
            <MainButton
              rounded
              size="xsmall"
              forceWidth={size.width > responsiveSizeTimesheet ? '204px' : '100%'}
              action={() => {
                setShowCurrency(false);
              }}
            >
              {fxRate > 0 ? t('common.general.update') : t('common.general.save')}
            </MainButton>
          </div>
        </NotificationModal>
        {expenseData?.CategoryId !== 5 &&
          category.value !== 5 &&
          expenseData?.FxCurrency?.CurrencyName &&
          (currency || expenseData?.FxCurrency?.CurrencyName) !== expenseCurrency && (
            <button onClick={() => setShowCurrency(true)} className={classes.forexRate}>
              {t('common.general.updaterate')}
            </button>
          )}
      </div>
      <div className={classes.mobileItemWrapperSection}>
        <h5 className={classes.title}>{t('dashboard.expenses.table.date')}*</h5>
        <div className={classes.content}>
          <CustomDayPicker
            date={expenseData?.Date}
            dayFormat="DD MMM"
            changeHandler={(newValue) =>
              setExpenseData((prev) => ({
                ...prev,
                Date: dayjs(newValue).format(generalEnums.DATEFORMAT)
              }))
            }
            sx={{
              width: '100%',
              background: colors.NAVY_BLUE,
              borderRadius: '100px',
              height: '35px',
              '& .MuiOutlinedInput-input.MuiInputBase-input': {
                color: colors.WHITE,
                textAlign: 'center'
              },
              '& .MuiSvgIcon-root': {
                color: colors.WHITE
              },
              '& .MuiOutlinedInput-root.MuiInputBase-root': {
                borderRadius: '100px',
                height: '100%'
              }
            }}
          />
        </div>
      </div>
      <div className={classes.mobileItemWrapperSection}>
        <h5 className={classes.title}>{t('dashboard.expenses.table.comment')}*</h5>
        <div className={classes.content}>
          <TextField
            placeholder={`${t('dashboard.createclaim.steps.comment')}*`}
            value={expenseData?.Comment}
            onChange={(e) => {
              setExpenseData((prev) => ({
                ...prev,
                Comment: e.target.value
              }));
            }}
            sx={textAreaStyles()}
          />
        </div>
      </div>
      <div className={classes.mobileItemWrapperSection}>
        <h5 className={classes.title}>{t('dashboard.expenses.table.receipt')}*</h5>
        <div className={classes.content}>
          <CommonFileUploader
            uploadHandler={(file) => uploadHandler(file, expenseData)}
            isMultiple
            acceptedFormats={['.jpeg', '.jpg', '.png', '.PNG', '.pdf', '.doc', '.docx', '.csv', '.xls', 'xlsx']}
            maxFileSizeMB={5}
            allowedQtyOfAttachments={10}
            existingAttachmentsQty={expenseData?.ExpenseAttachments?.length}
          />
        </div>
        <div className={classes.attachmentsList}>
          {expenseData?.ExpenseAttachments?.map((attach) => (
            <AttachmentItem
              key={attach.Id}
              label={`${attach.FileAttachmentName || attach.name}`}
              deleteHandler={() => deleteHandler(attach)}
            />
          ))}
        </div>
        {expenseData?.ExpenseAttachments?.length === 0 && (
          <div className={classes.checkboxContainer}>
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={(e) => handleNoReceipt(e)} />}
              label={t('dashboard.createclaim.steps.noreceipt')}
              sx={{ margin: 0, 'MuiFormControlLabel-label': { fontSize: 9 } }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
